import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    KeyboardSensor,
    type DragEndEvent,
    type DragStartEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { memo, useId, useState, type FC, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { EnvironmentClassItem } from "@/components/EnvironmentClassItem";
import { EnvironmentClassSelect } from "@/components/EnvironmentClassSelect";

type EnvironmentClassOrderedListProps = {
    environmentClassEntries?: EnvironmentClassEntry[];
    allEnvironmentClassEntries?: EnvironmentClassEntry[];
    label: string;
    loading?: boolean;
    disabled?: boolean;
    minItems?: number;
    maxItems?: number;
    onChange: (reorderedEntries: EnvironmentClassEntry[]) => void;
    showPrebuildColumn?: boolean;
    prebuildSelectedIds?: string[];
    onPrebuildSelectionChange?: (ids: string[]) => void;
    /** Show warm pool and prebuild status columns */
    showWarmPoolColumn?: boolean;
    showPrebuildStatusColumn?: boolean;
    /** Render warm pool controls for a given env class ID */
    renderWarmPoolCell?: (envClassId: string) => ReactNode;
    /** Render prebuild status for a given env class ID */
    renderPrebuildStatusCell?: (envClassId: string) => ReactNode;
};

export const EnvironmentClassOrderedList: FC<EnvironmentClassOrderedListProps> = memo(
    ({
        environmentClassEntries,
        allEnvironmentClassEntries,
        label,
        loading,
        disabled,
        minItems,
        maxItems,
        onChange,
        showPrebuildColumn,
        prebuildSelectedIds,
        onPrebuildSelectionChange,
        showWarmPoolColumn,
        showPrebuildStatusColumn,
        renderWarmPoolCell,
        renderPrebuildStatusCell,
    }) => {
        const elId = useId();
        const [activeId, setActiveId] = useState<string | null>(null);

        const sensors = useSensors(
            useSensor(PointerSensor, {
                activationConstraint: {
                    distance: 8,
                },
            }),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            }),
        );

        const handleDragStart = (event: DragStartEvent) => {
            const { active } = event;
            setActiveId(active.id as string);
        };

        const handleDragEnd = (event: DragEndEvent) => {
            if (!environmentClassEntries) {
                return;
            }

            const { active, over } = event;
            setActiveId(null);

            if (!over || active.id === over.id) {
                return;
            }

            const oldIndex = environmentClassEntries.findIndex(
                (entry) => `${entry.clazz.runnerId}:${entry.clazz.id}` === active.id,
            );
            const newIndex = environmentClassEntries.findIndex(
                (entry) => `${entry.clazz.runnerId}:${entry.clazz.id}` === over.id,
            );

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                onChange(arrayMove(environmentClassEntries, oldIndex, newIndex));
            }
        };

        const activeEntry = activeId
            ? environmentClassEntries?.find((entry) => `${entry.clazz.runnerId}:${entry.clazz.id}` === activeId)
            : undefined;

        // Filter available environment classes (exclude ones already in the list)
        const availableEnvironmentClasses =
            allEnvironmentClassEntries && environmentClassEntries
                ? allEnvironmentClassEntries.filter(
                      (entry) =>
                          !environmentClassEntries.some(
                              (existingEntry) =>
                                  existingEntry.clazz.runnerId === entry.clazz.runnerId &&
                                  existingEntry.clazz.id === entry.clazz.id,
                          ),
                  )
                : [];

        const handleAddEnvironmentClass = (entry: EnvironmentClassEntry | undefined) => {
            if (!environmentClassEntries || !entry) {
                return;
            }
            // Check if we're at the maximum number of items
            if (maxItems && environmentClassEntries.length >= maxItems) {
                return;
            }
            onChange([...environmentClassEntries, entry]);
        };

        const handleRemoveEnvironmentClass = (entryToRemove: EnvironmentClassEntry) => {
            if (!environmentClassEntries) {
                return;
            }
            // Check if we're at the minimum number of items
            if (minItems && environmentClassEntries.length <= minItems) {
                return;
            }
            const filteredEntries = environmentClassEntries.filter(
                (entry) =>
                    !(
                        entry.clazz.runnerId === entryToRemove.clazz.runnerId &&
                        entry.clazz.id === entryToRemove.clazz.id
                    ),
            );
            onChange(filteredEntries);
        };

        const handlePrebuildToggle = (envClassId: string, checked: boolean) => {
            if (!onPrebuildSelectionChange || !prebuildSelectedIds) return;
            if (checked) {
                onPrebuildSelectionChange([...prebuildSelectedIds, envClassId]);
            } else {
                onPrebuildSelectionChange(prebuildSelectedIds.filter((id) => id !== envClassId));
            }
        };

        const currentCount = environmentClassEntries?.length || 0;
        const isAtMaxItems = maxItems ? currentCount >= maxItems : false;
        const isAtMinItems = minItems ? currentCount <= minItems : false;
        const showEnvironmentClassSelect =
            allEnvironmentClassEntries && availableEnvironmentClasses.length > 0 && !isAtMaxItems;

        const hasExtraColumns = showPrebuildColumn || showWarmPoolColumn || showPrebuildStatusColumn;

        return (
            <div className="flex w-full flex-col gap-2">
                <div className="flex items-center">
                    <Label htmlFor={elId} className="font-medium">
                        {label}
                    </Label>
                    {hasExtraColumns && (
                        <div className="ml-auto hidden items-center gap-0 text-sm font-medium text-content-primary lg:flex">
                            {showPrebuildColumn && <span className="w-[72px] text-center">Prebuild</span>}
                            {showWarmPoolColumn && (
                                <Tooltip
                                    content={
                                        <span>
                                            Keep pre-built environments running so new ones start
                                            instantly. Each warm instance incurs compute costs while
                                            idle.{" "}
                                            <ExternalLink
                                                href="https://ona.com/docs/ona/projects/warm-pools"
                                                className="text-content-tooltip underline"
                                            >
                                                Learn more
                                            </ExternalLink>
                                        </span>
                                    }
                                    className="max-w-[240px]"
                                    usePortal
                                >
                                    <span className="w-[140px] cursor-help text-center underline decoration-dotted underline-offset-4">
                                        Warm pool
                                    </span>
                                </Tooltip>
                            )}
                            {showPrebuildStatusColumn && (
                                <span className="w-[120px] text-center">Prebuild status</span>
                            )}
                            {/* Invisible spacer mirroring the remove button layout in EnvironmentClassItem
                                (pl-2 + size-6 button) so headers align structurally with row content.
                                The outer px-4 on item rows is matched by this living inside the same flex container. */}
                            {!isAtMinItems && <div className="w-6 shrink-0 pl-2" />}
                            {/* Account for item row px-4 right padding */}
                            <div className="w-4 shrink-0" />
                        </div>
                    )}
                </div>

                {loading || !environmentClassEntries ? (
                    <div className="flex items-center justify-center rounded-lg border border-border-base bg-surface-pure px-5 py-4">
                        <span className="text-content-tertiary">Loading environment classes...</span>
                    </div>
                ) : (
                    environmentClassEntries.length > 0 && (
                        <DndContext
                            sensors={sensors}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        >
                            <div
                                id={elId}
                                className={cn(
                                    "rounded-lg border border-border-base bg-surface-pure",
                                    disabled && "pointer-events-none opacity-50",
                                )}
                            >
                                <SortableContext
                                    items={environmentClassEntries.map(
                                        (entry) => `${entry.clazz.runnerId}:${entry.clazz.id}`,
                                    )}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {environmentClassEntries.map((environmentClassEntry) => {
                                        const id = `${environmentClassEntry.clazz.runnerId}:${environmentClassEntry.clazz.id}`;
                                        const envClassId = environmentClassEntry.clazz.id;
                                        return (
                                            <EnvironmentClassItem
                                                key={id}
                                                id={id}
                                                environmentClassEntry={environmentClassEntry}
                                                disabled={disabled}
                                                isDraggable={true}
                                                className={"border-b border-border-light last:border-b-0"}
                                                onRemove={
                                                    isAtMinItems
                                                        ? undefined
                                                        : () => handleRemoveEnvironmentClass(environmentClassEntry)
                                                }
                                                showPrebuildCheckbox={showPrebuildColumn}
                                                isPrebuildSelected={prebuildSelectedIds?.includes(envClassId)}
                                                onPrebuildToggle={(checked) =>
                                                    handlePrebuildToggle(envClassId, checked)
                                                }
                                                warmPoolCell={
                                                    showWarmPoolColumn
                                                        ? renderWarmPoolCell?.(envClassId)
                                                        : undefined
                                                }
                                                prebuildStatusCell={
                                                    showPrebuildStatusColumn
                                                        ? renderPrebuildStatusCell?.(envClassId)
                                                        : undefined
                                                }
                                            />
                                        );
                                    })}
                                </SortableContext>
                            </div>

                            <DragOverlay>
                                {activeEntry && (
                                    <div className="rounded-lg border border-border-base bg-surface-primary shadow-lg">
                                        <EnvironmentClassItem
                                            id={`${activeEntry.clazz.runnerId}:${activeEntry.clazz.id}`}
                                            environmentClassEntry={activeEntry}
                                            disabled={disabled}
                                            isDraggable={true}
                                            className="border-b-0 shadow-lg"
                                        />
                                    </div>
                                )}
                            </DragOverlay>
                        </DndContext>
                    )
                )}

                {showEnvironmentClassSelect && (
                    <EnvironmentClassSelect
                        key={availableEnvironmentClasses.map((e) => `${e.clazz.runnerId}:${e.clazz.id}`).join(",")}
                        environmentClassEntries={availableEnvironmentClasses}
                        loading={false}
                        disabled={disabled || false}
                        name="add-environment-class"
                        onChange={handleAddEnvironmentClass}
                        placeholder={
                            <div className="flex h-14 w-full items-center gap-2 px-6">
                                <Plus size={16} className="text-content-primary" />
                                <span className="text-content-primary">
                                    {availableEnvironmentClasses.length > 0
                                        ? "Add environment class"
                                        : "No available environment classes"}
                                </span>
                            </div>
                        }
                    />
                )}
            </div>
        );
    },
);

EnvironmentClassOrderedList.displayName = "EnvironmentClassOrderedList";
